/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { useTheme } from "@mui/material";
import React from "react";
import { Box } from "../Box";
import { Paper } from "../Paper";
import { Typography } from "../Typography";
import { StatisticsCardProps } from "./IStatCard";

const StatisticsCard: React.FC<StatisticsCardProps> = ({
  title,
  hint,
  number,
  percentage,
  color,
  sxStyles,
  state
}) => {
  const theme = useTheme();
  const getCardStyles = () => {
    switch (state) {
      case "Positive":
        return {
          backgroundColor: "#E8F5E9",
          border: "2px solid #4CAF50",
          borderLeft: "6px solid #4CAF50",
          borderRadius: "8px 0 0 8px",
        };
      case "Neutral":
        return {
          backgroundColor: "#F5F5F5",
          border: "2px solid #E0E0E0",
          borderLeft: "6px solid #E0E0E0",
          borderRadius: "8px 0 0 8px",
        };
      case "Pending":
        return {
          backgroundColor: "#FFF8E1",
          border: "2px solid #FFD54F",
          borderLeft: "6px solid #FFD54F",
          borderRadius: "8px 0 0 8px",
        };
      case "Negative":
        return {
          backgroundColor: "#FEEBEE",
          border: "2px solid #EF5350",
          borderLeft: "6px solid #EF5350",
          borderRadius: "8px 0 0 8px",
        };
      case "SecondaryInfo":
        return {
          backgroundColor: "#E3F2FD",
          border: "2px solid #64B5F6",
          borderLeft: "6px solid #64B5F6",
          borderRadius: "8px 0 0 8px",
        }
      default:
        return {
          backgroundColor: "#E8F5E9",
          border: "2px solid #4CAF50",
          borderLeft: "6px solid #4CAF50",
          borderRadius: "8px 0 0 8px",
        };
    }
  };
  return (
    <Paper
      sx={{
        ...sxStyles,
        backgroundColor: color || theme.palette.background.default,
        elevation: 0,
        boxShadow: "none",
        borderRadius: "1rem",
      }}
    >
      <Box
        sx={{
          ...getCardStyles(),
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
        p={2}>
        {/* Title */}
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            color: '#000',
            whiteSpace: "normal", // Allow wrapping
            wordWrap: "break-word",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {/* Optional Hint */}
        {hint && (
          <Typography
            variant="body2"
            sx={{
              color: '#000',
              marginBottom: theme.spacing(2),
            }}
          >
            {hint}
          </Typography>
        )}

        {/* Value and Percentage */}
        <Box
          display="flex"
          alignItems="baseline"
          justifyContent="space-between"
        >
          {/* Statistic Number */}
          <Typography
            variant="h4"
            sx={{
              marginRight: theme.spacing(1),
              color: '#000',
            }}
          >
            {number}
          </Typography>

          {/* Percentage */}
          {percentage && (
            <Typography
              variant="body1"
              sx={{
                color: '#000',
              }}
            >
              ({percentage}%)
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default StatisticsCard;
